import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Tabs,
	Tab,
	Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	useGetMyAllProjectPositionLazyQuery,
	useGetMyProjectTimeSummariesLazyQuery,
	EmailType,
	type ProjectTimeSummary,
	type ProjectWithPositions,
	type EmailInformation,
} from "@repo/server-api";
import { MonthSlider } from "@repo/shared/components";
import type { MonthValue } from "@repo/shared/components";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useEffect, useState } from "react";

import ProjectProgressPieBox from "../../components/ProjectProgressPieBox";

import { DayBasedTimeTracking } from "../../components";

// Type for additionalInformation items that include __typename from GraphQL
type AdditionalInfoWithTypename = { __typename?: string; id: string } & Partial<EmailInformation>;

// Helper function to extract the first work email from additionalInformation
const getWorkEmail = (additionalInfo?: AdditionalInfoWithTypename[]): string | undefined => {
	if (!additionalInfo) return undefined;

	const workEmail = additionalInfo.find(
		(info) => info.__typename === "EmailInformation" && info.emailType === EmailType.Work
	);

	if (workEmail && workEmail.__typename === "EmailInformation" && workEmail.emailAddress) {
		return workEmail.emailAddress;
	}

	return undefined;
};

export const MyTimeTracking = () => {
	const { t, i18n } = useTranslation();

	const [projects, setProjects] = useState<ProjectWithPositions[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

	// MonthSlider state lifted from DayBasedTimeTracking
	const [selectedMonth, setSelectedMonth] = useState<MonthValue>({
		year: dayjs().year(),
		month: dayjs().month() + 1,
	});

	const startDate = dayjs().year(selectedMonth.year).month(selectedMonth.month - 1).startOf("month").format("YYYY-MM-DD");
	const endDate = dayjs().year(selectedMonth.year).month(selectedMonth.month - 1).endOf("month").format("YYYY-MM-DD");

	const [getAllProjectPositions] = useGetMyAllProjectPositionLazyQuery({
		fetchPolicy: "no-cache",
	});

	const refreshData = () => {
		getAllProjectPositions({
			variables: {
				startDate: startDate,
				endDate: endDate,
			},
		}).then((res) => {
			if (res.data && res.data.myAllProjectPosition) {
				const projectList = res.data.myAllProjectPosition as ProjectWithPositions[];
				// Sort projects by name
				const sortedProjects = [...projectList].sort((a, b) =>
					a.projectName.localeCompare(b.projectName)
				);
				setProjects(sortedProjects);

				// Auto-select first project if none selected
				if (!selectedProjectId && sortedProjects.length > 0) {
					setSelectedProjectId(sortedProjects[0]!.projectId);
				}
			}
		});
	};

	const [getProjectTimeSummaries] = useGetMyProjectTimeSummariesLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data.myProjectTimeSummaries) {
				setProjectTimeSummary(data.myProjectTimeSummaries as ProjectTimeSummary[]);
			}
		},
	});

	const [projectTimeSummaries, setProjectTimeSummary] = useState<ProjectTimeSummary[] | null>(null);

	const refreshProjectSummaries = () => {
		if (selectedProjectId) {
			getProjectTimeSummaries({
				variables: {
					projectId: selectedProjectId,
				},
			});
		}
	};

	useEffect(() => {
		if (selectedProjectId) {
			getProjectTimeSummaries({
				variables: {
					projectId: selectedProjectId,
				},
			});
		} else {
			setProjectTimeSummary(null);
		}
	}, [selectedProjectId]);

	useEffect(() => {
		refreshData();
	}, [selectedMonth, t, i18n.language]);

	const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
		setSelectedProjectId(newValue);
	};

	const selectedProject = projects.find((p) => p.projectId === selectedProjectId);
	const isSingleProject = projects.length === 1;

	const titleText = isSingleProject && selectedProject
		? `${t("my-time-entries")} — ${selectedProject.projectName}`
		: t("my-time-entries");

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%", px: 2 }}>
			{/* Compact header row: Title left + MonthSlider right */}
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
				<Typography variant="h6" sx={{ color: "primary.main", fontWeight: "bold" }}>
					{titleText}
				</Typography>
				<MonthSlider value={selectedMonth} onChange={setSelectedMonth} />
			</Box>

			{/* No projects message */}
			{projects.length === 0 ? (
				<Box sx={{ textAlign: "center", py: 5 }}>
					<Typography variant="h6" color="text.secondary">
						{t("no-projects-assigned")}
					</Typography>
				</Box>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
					{/* Project Tabs - only show if multiple projects */}
					{projects.length > 1 && (
						<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
							<Tabs
								value={selectedProjectId || false}
								onChange={handleTabChange}
								variant="scrollable"
								scrollButtons="auto"
								sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}>
								{projects.map((project) => (
									<Tab key={project.projectId} label={project.projectName} value={project.projectId} />
								))}
							</Tabs>
						</Box>
					)}

					{/* Collapsible positions overview */}
					{projectTimeSummaries && (
						<Accordion
							defaultExpanded={true}
							disableGutters
							sx={{
								boxShadow: "none",
								"&:before": { display: "none" },
								"& .MuiAccordionSummary-root": { minHeight: 36, py: 0 },
								"& .MuiAccordionSummary-content": { my: 0.5 },
							}}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="body2" sx={{ fontWeight: 500, color: "primary.main" }}>
									{t("available-positions")}
								</Typography>
							</AccordionSummary>
							<AccordionDetails sx={{ p: 0 }}>
								<ProjectProgressPieBox projectTimeSummaries={projectTimeSummaries} />
							</AccordionDetails>
						</Accordion>
					)}

					{/* Render calendar for all projects to preserve state */}
					{projects.map((project) => (
						<Box
							key={project.projectId}
							role="tabpanel"
							hidden={selectedProjectId !== project.projectId}
							sx={{
								display: selectedProjectId === project.projectId ? "flex" : "none",
								flexDirection: "column",
								flex: 1,
								minHeight: 0,
							}}>
							<DayBasedTimeTracking
								projectId={project.projectId}
								projectPositions={project.positions || []}
								timeTrackingResolution={project.timeTrackingResolution || 15}
								projectStartDate={project.start}
								projectEndDate={project.end}
								isProjectInvalid={project.isProjectInvalid}
								projectLeaderName={project.projectLeader?.displayName}
								projectLeaderEmail={getWorkEmail(project.projectLeader?.additionalInformation as AdditionalInfoWithTypename[] | undefined)}
								selectedMonth={selectedMonth}
								onProjectSummariesRefresh={refreshProjectSummaries}
							/>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};
